import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { AuthTheme } from '@/components/auth/constants';

interface AuthThemeContextValue {
  authTheme: AuthTheme;
  setAuthTheme: (theme: AuthTheme) => void;
}

const AuthThemeContext = createContext<AuthThemeContextValue | null>(null);

/**
 * Hook to access auth theme (for login/register pages before user is authenticated)
 */
export function useAuthTheme(): AuthThemeContextValue {
  const context = useContext(AuthThemeContext);
  if (!context) {
    throw new Error('useAuthTheme must be used within AuthThemeProvider');
  }
  return context;
}

interface AuthThemeProviderProps {
  children: ReactNode;
}

/**
 * Detects initial auth theme from URL parameter or localStorage
 */
function getInitialAuthTheme(): AuthTheme {
  // Check URL parameter first
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const urlTheme = params.get('theme');
    if (urlTheme === 'drboese' || urlTheme === 'invol') {
      // Persist to localStorage
      localStorage.setItem('authTheme', urlTheme);
      return urlTheme;
    }

    // Fall back to localStorage
    const storedTheme = localStorage.getItem('authTheme');
    if (storedTheme === 'drboese' || storedTheme === 'invol') {
      return storedTheme;
    }
  }

  return 'invol'; // Default theme
}

/**
 * Provider for auth page theme (separate from main app theme)
 * Used on login/register pages before user is authenticated
 */
export function AuthThemeProvider({ children }: AuthThemeProviderProps) {
  const [authTheme, setAuthThemeState] = useState<AuthTheme>(getInitialAuthTheme);

  const setAuthTheme = useCallback((theme: AuthTheme) => {
    setAuthThemeState(theme);
    // Persist to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('authTheme', theme);
    }
  }, []);

  return (
    <AuthThemeContext.Provider value={{ authTheme, setAuthTheme }}>
      {children}
    </AuthThemeContext.Provider>
  );
}

export { AuthThemeContext };
